/**
 * Halloween quote prints — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenQuotePrintsPack: ToilePack = {
    slug: 'halloween-quote-prints',
    category: 'season',
    name: 'Halloween quote prints',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'line', label: 'Spooky line', subject: 'A vertical print lettered "Something wicked this way comes" in a serif with a crescent moon and nothing else'},
        {key: 'boo', label: 'One word', subject: 'A vertical print with the single word "BOO" lettered large and a small ghost beneath'},
        {key: 'motto', label: "Witch's motto", subject: 'A vertical print lettered "Drink up witches" in script inside a cauldron outline'},
    ],
    styles: [
        {key: 'cute', label: 'Spooky cute', style: 'kawaii spooky illustration, rounded shapes, soft outlines, friendly faces', palette: 'Pastel purple, orange, mint and cream with black accents', framing: 'Centered on a solid background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: '1950s halloween paper decoration style, halftone grain, slight misregistration', palette: 'Orange, black, cream and a faded green', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'bold', label: 'Bold graphic', style: 'bold graphic illustration, thick outlines, high contrast, flat fills', palette: 'Orange, black and white with a single purple', framing: 'Centered on a solid background with a little room around it'},
    ],
    directions: ['in a round frame', 'as a two-line quote', 'for a nursery wall'],
    directionHint: 'try: for a nursery wall instead',
    seo: {
        title: 'Halloween Quote Prints: Spooky Line, Boo, Motto',
        metaDescription: "Nine halloween quote prints -- spooky line, one-word boo, witch's motto -- in spooky cute, vintage, and bold graphic finishes.",
        h1: "Halloween quote print: spooky line, one-word boo, and witch's motto",
        tagline: 'Nine prints, three lettered lines, three finishes each.',
        intro: "Nine typographic halloween quote prints: a 'Something wicked this way comes' line beneath a crescent moon, a single lettered 'BOO' with a small ghost, and a 'Drink up witches' motto inside a cauldron outline. Three finishes -- spooky cute, vintage, and bold graphic -- for framed wall art or a printable gift. Ask for a round frame instead, or a two-line quote, and the same three finishes carry it.",
        useCases: [
            'a framed wall print',
            'a printable halloween gift',
            'a mantel or gallery wall display',
            'a party invitation insert',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: "Nine lettered prints -- a spooky line, a single word, and a witch's motto -- each in spooky cute, vintage, and bold graphic finishes, quiet and typographic."},
            {q: 'What size frame does this fit?', a: 'The prints render at a tall portrait ratio, matching a standard 8x10in or 11x14in frame -- scale to your exact size at printing.'},
            {q: 'Can I change the wording?', a: 'Not on this pack -- the lettering is fixed per design, but the direction field can swap the whole quote or add a second line.'},
            {q: "Is this good for a nursery or kids' room?", a: 'Yes -- the direction field can retarget any design for a nursery wall while keeping the same typographic register.'},
        ],
    },
}
